import type { ServiceResult } from "@core/services/serviceResult";
import type { UserProfile, UserProfileLegacy } from "@core/types/models";

import type { XhrStatus } from "@common/http-client";
import httpClient from "@common/http-client";
import { resolveServiceErrorKey } from "@common/utils/http/errors";

export interface ProfileValidationErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  languageTag?: string;
}

export type ProfileUpdateResult = ServiceResult<UserProfileLegacy> & {
  validationErrors?: ProfileValidationErrors;
};

const mapUpdateValidationErrors = (error: unknown): ProfileValidationErrors | undefined => {
  if (typeof error !== "string") {
    return undefined;
  }

  const code = error.trim();
  if (code.length === 0) {
    return undefined;
  }

  switch (code) {
    case "ERR_INVALID_EMAIL":
      return { email: "profile.validation.emailInvalid" };
    case "ERR_EMAIL_EXISTS":
      return { email: "profile.validation.emailExists" };
    case "ERR_INVALID_LANGUAGETAG":
      return { languageTag: "profile.validation.language" };
    case "ERR_INVALID_FIRST_NAME":
      return { firstName: "profile.validation.firstName" };
    case "ERR_INVALID_LAST_NAME":
      return { lastName: "profile.validation.lastName" };
    case "ERR_INVALID_PHONENUMBER":
      return { phoneNumber: "profile.validation.phoneNumberRequired" };
    default:
      return undefined;
  }
};

const toServiceResult = <T = void>(response: XhrStatus<T>, fallbackError?: string): ServiceResult<T> => {
  if (!response.ok) {
    const errorKey = fallbackError
      ? resolveServiceErrorKey(response.errorThrown, fallbackError)
      : resolveServiceErrorKey(response.errorThrown);
    return { success: false, errorKey };
  }

  return { success: true, data: response.data };
};

export const profileService = {
  async fetchProfile() {
    const config = await httpClient.getUnwrapped<{ userProfile: UserProfile }>("/dashboard?handler=config");
    return config.userProfile;
  },

  async logoutDevice(deviceId: string): Promise<ServiceResult<{ signout: boolean }>> {
    const response = await httpClient.delete<{ signout: boolean }>("/api/account/fingerprint", { id: deviceId });
    if (!response.ok) {
      return { success: false, errorKey: "profile.errorLogoutDevice" };
    }
    return { success: true, data: { signout: Boolean(response.data?.signout) } };
  },

  async updateProfile(updates: Partial<UserProfile>): Promise<ProfileUpdateResult> {
    const updatePayload = {
      firstname: updates.firstName ?? "",
      middlename: updates.middleName ?? "",
      lastname: updates.lastName ?? "",
      email: updates.email ?? "",
      languageTag: updates.languageTag ?? "",
      emailTransactionsAfterPurchase: updates.emailTransactionsAfterPurchase ?? false,
      analyticsEnabled: updates.analyticsEnabled ?? false
    };

    try {
      const response = await httpClient.post<UserProfileLegacy | string>("/account/update", updatePayload);
      const requestValidationErrors = mapUpdateValidationErrors(response.errorThrown);
      if (!response.ok) {
        if (requestValidationErrors) {
          return { success: false, validationErrors: requestValidationErrors };
        }
        return { success: false, errorKey: resolveServiceErrorKey(response.errorThrown, "profile.saveErrorMessage") };
      }

      const payload = response.data;
      if (typeof payload === "string") {
        const payloadValidationErrors = mapUpdateValidationErrors(payload);
        if (payloadValidationErrors) {
          return { success: false, validationErrors: payloadValidationErrors };
        }
        return { success: false, errorKey: resolveServiceErrorKey(payload, "profile.saveErrorMessage") };
      }

      if (payload === null || payload === undefined || typeof payload !== "object") {
        return { success: false, errorKey: "profile.saveErrorMessage" };
      }

      return { success: true, data: payload };
    } catch (error) {
      const caughtValidationErrors = mapUpdateValidationErrors(error);
      if (caughtValidationErrors) {
        return { success: false, validationErrors: caughtValidationErrors };
      }
      return { success: false, errorKey: "profile.saveErrorMessage" };
    }
  },

  async sendChangePhoneTfa(phoneNumber: string) {
    try {
      const response = await httpClient.get<{ name: string | null; tfaChallenge: string } | string>(
        "/account/changephonenumbertfa",
        {
          phoneNumber
        }
      );

      if (!response.ok) {
        return { success: false, errorKey: resolveServiceErrorKey(response.errorThrown) };
      }

      const payload = response.data;
      if (typeof payload === "string" || !payload?.tfaChallenge) {
        return { success: false, errorKey: resolveServiceErrorKey(payload, "profile.errors.phoneChangeErrorUnknown") };
      }

      return { success: true, data: payload };
    } catch {
      return { success: false, errorKey: "profile.errors.phoneChangeErrorUnknown" };
    }
  },

  async confirmChangePhoneTfa(phoneNumber: string, tfaCode: string, tfaChallenge: string) {
    try {
      const response = await httpClient.get<{ phoneNumberFormatted: string } | string>(
        "/account/changephonenumbertfaconfirm",
        {
          phoneNumber,
          tfaCode,
          tfaChallenge
        }
      );

      if (!response.ok) {
        return { success: false, errorKey: resolveServiceErrorKey(response.errorThrown) };
      }

      const payload = response.data;
      if (typeof payload === "string" || !payload?.phoneNumberFormatted) {
        return { success: false, errorKey: resolveServiceErrorKey(payload, "profile.errors.phoneChangeErrorUnknown") };
      }

      return { success: true, data: payload };
    } catch {
      return { success: false, errorKey: "profile.errors.phoneChangeErrorUnknown" };
    }
  },

  async sendDeleteAccountTfa() {
    try {
      const response = await httpClient.delete<void>("/account/deleteaccount");
      return toServiceResult<void>(response, "profile.saveErrorMessage");
    } catch {
      return { success: false, errorKey: "profile.saveErrorMessage" };
    }
  },

  async confirmDeleteAccountTfa(tfaCode: string) {
    try {
      const response = await httpClient.delete<void>("/account/deleteaccounttfaconfirm", { tfaCode });
      return toServiceResult<void>(response, "profile.saveErrorMessage");
    } catch {
      return { success: false, errorKey: "profile.saveErrorMessage" };
    }
  },

  async setPlusAutomaticRenewal(enabled: boolean) {
    const response = await httpClient.post<{ success: boolean }>("/account/plusrenewal", { enabled });
    if (!response.ok) {
      if (response.errorThrown === "ERR_PLUS_SUBSCRIPTION_INVALID_AND_RENEWAL_IS_DISABLED") {
        return { success: false, errorKey: "profile.errorPlusSubscriptionInvalid" };
      }
      if (response.errorThrown === "ERR_NO_VALID_PAYMENT_SOURCE_WITH_PLUS_ENABLED") {
        return { success: false, errorKey: "profile.errorNoValidPaymentSource" };
      }
      return { success: false, errorKey: "profile.errorUpdatePlus" };
    }
    return { success: true };
  },

  async setLanguage(tag: string) {
    try {
      const response = await httpClient.post<void>(`/?handler=setLanguage&tag=${encodeURIComponent(tag)}`, {});
      if (!response.ok) {
        return { success: false, errorKey: resolveServiceErrorKey(response.errorThrown) };
      }
      return { success: true };
    } catch (error) {
      return { success: false, errorKey: resolveServiceErrorKey(error) };
    }
  },

  async confirmContactInfo() {
    try {
      const response = await httpClient.post<boolean>("/account/confirmcontactinfo", {});
      return toServiceResult<boolean>(response, "profile.saveErrorMessage");
    } catch {
      return { success: false, errorKey: "profile.saveErrorMessage" };
    }
  },

  async signOutAllDevices() {
    try {
      const response = await httpClient.delete<void>("/account/removefingerprints");
      return toServiceResult<void>(response, "profile.saveErrorMessage");
    } catch {
      return { success: false };
    }
  }
};
